import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside, onKeyStroke } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { TutorBillingFlag } from './enums'
import type { TutorBillingTriStateOption } from './interfaces'
import type { TriState } from './types'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'

import { useTutorBillingStore } from './store'
import { TUTOR_BILLING_FLAG_LABELS, TUTOR_BILLING_FLAGS } from '../config/tutor-billing'

const TRI_STATE_OPTIONS: TutorBillingTriStateOption[] = [
  { text: 'No', value: false, tone: 'no' },
  { text: 'All', value: null, tone: 'neutral' },
  { text: 'Yes', value: true, tone: 'yes' },
]

interface FilterOpsStatusReturn {
  isOpen: Ref<boolean>
  labelId: string
  valueId: string
  containerRef: Ref<HTMLElement | null>
  triggerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  flags: TutorBillingFlag[]
  labels: Record<TutorBillingFlag, string>
  stateOptions: TutorBillingTriStateOption[]
  getValue: (flag: TutorBillingFlag) => TriState
  setValue: (flag: TutorBillingFlag, value: TriState) => void
  summary: ComputedRef<string>
  chevronIcon: ComputedRef<IconDefinition>
  setToggle: () => void
}

export const useFilterOpsStatus = (): FilterOpsStatusReturn => {
  const store = useTutorBillingStore()

  const isOpen = ref<boolean>(false)
  const baseId = `filter-ops-status-${useId()}`
  const labelId = `${baseId}-label`
  const valueId = `${baseId}-value`
  const containerRef = ref<HTMLElement | null>(null)
  const triggerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const summary = computed<string>(() => {
    const active = TUTOR_BILLING_FLAGS.filter(flag => store.getFilters.flags[flag] !== null).length
    return active ? `${active} active` : 'All'
  })

  const chevronIcon = computed<IconDefinition>(() => isOpen.value ? faChevronUp : faChevronDown)

  const getValue = (flag: TutorBillingFlag): TriState => store.getFilters.flags[flag]

  const setValue = (flag: TutorBillingFlag, value: TriState): void => {
    store.setFilterFlag(flag, value)
  }

  const setToggle = (): void => {
    isOpen.value = !isOpen.value
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  onKeyStroke('Escape', () => {
    if (!isOpen.value) return

    isOpen.value = false
    triggerRef.value?.focus()
  })

  return {
    isOpen,
    labelId,
    valueId,
    containerRef,
    triggerRef,
    floatingRef,
    floatingStyles,
    flags: TUTOR_BILLING_FLAGS,
    labels: TUTOR_BILLING_FLAG_LABELS,
    stateOptions: TRI_STATE_OPTIONS,
    getValue,
    setValue,
    summary,
    chevronIcon,
    setToggle,
  }
}
